
import { Component, Vue, Watch } from 'vue-property-decorator';
import { mockData } from '../../mocks/VariacaoSalarialMock';
import { Mutation } from 'vuex-class';
import User from '@/../bower_components/teknisavuecomponents/assets/interfaces/User';
import IndicadoresController from '@/assets/ts/controller/IndicadoresController';
import DateUtil from '@/../bower_components/teknisavuecomponents/assets/utils/DateUtil';
import { CacheService } from '@/assets/ts/services/CacheService';
import VueApexCharts from 'vue-apexcharts';

@Component({
  name: 'IndicadoresVariacaoSalarial',
  components: {
    apexchart: VueApexCharts,
  },
})
export default class IndicadoresVariacaoSalarial extends Vue {
  private readonly viewTitle = 'Médias Salariais';
  private loading = false;
  private stateData: any = {};
  private lastUpdate: string | null = null;

  @Mutation('changeCurrentView')
  private changeCurrentView!: (view: string) => void;

  private states = mockData.states;

  private get user(): User {
    return this.$store.getters.user;
  }

  private cboHeaders = [
    { text: 'CBO', value: 'codigo' },
    { text: 'Descrição', value: 'descricao' },
    { text: 'Menor Salário', value: 'menorSalarioOrg' },
    { text: 'Média', value: 'mediaSalarialOrg' },
    { text: 'Maior Salário', value: 'maiorSalarioOrg' },
    { text: 'Média Estadual no Mercado', value: 'mediaSalarialGeral' },
    { text: 'Média Empresa X Média Mercado', value: 'diferencaPercentual' }
  ];

  created() {
    this.changeCurrentView(this.viewTitle);
    this.carregarDados();
    this.updateLastCacheDate();
  }

  private updateLastCacheDate() {
    const cacheKey = `comparativo_salarial_${this.user.nrorg}_${DateUtil.getDateCompetenc()}_${this.user.dp}`;
    this.lastUpdate = CacheService.getLastUpdateDate(cacheKey);
  }

  private async carregarDados() {
    this.loading = true;
    try {
      const dtmesCompetenc = DateUtil.getDateCompetenc();
      const dados = await IndicadoresController.getComparativoSalarialPorCBO(this.user.dp, dtmesCompetenc);
      this.stateData = (dados && 'stateData' in dados) ? (dados as { stateData: any }).stateData : {};
      this.states = dados.states;
      this.updateLastCacheDate();
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      this.loading = false;
    }
  }

  private getStateStats(stateCode: string) {
    if (!this.stateData || !this.stateData[stateCode]) {
      return {
        mediaSalarialOrg: 0,
        mediaSalarialGeral: 0,
        menorSalarioOrg: 0,
        menorSalarioGeral: 0,
        maiorSalarioOrg: 0,
        maiorSalarioGeral: 0,
        diferencaPercentual: 0,
        diferencaPercentualMaiorSalario: 0,
        diferencaPercentualMenorSalario: 0,
        totalCBOs: 0
      };
    }
    return this.stateData[stateCode].generalStats;
  }

  private getStateEstruturas(stateCode: string) {
    if (!this.stateData || !this.stateData[stateCode]) {
      return [];
    }
    return this.stateData[stateCode].estruturas || [];
  }

  private getVariationColor(variation: number): string {
    if (!variation) return 'warning';
    if (variation >= 0) return 'success';
    if (variation < -10) return 'error';
    return 'warning';
  }

  private formatCurrency(value: any): string {
    // Garante que o valor seja um número
    const numValue = Number(value);
    
    // Se não for um número válido, usa 0
    if (isNaN(numValue)) {
      value = 0;
    } else {
      value = numValue;
    }
    
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  }

  private formatPercentage(value: any): string {
    // Garante que o valor seja um número
    const numValue = Number(value);
    
    // Se não for um número válido, retorna '0.00%'
    if (isNaN(numValue)) {
      return '0.00%';
    }
    
    return `${numValue.toFixed(2)}%`;
  }

  private async recarregarDados() {
    this.loading = true;
    try {
      const dtmesCompetenc = DateUtil.getDateCompetenc();
      IndicadoresController.clearComparativoSalarialCache(this.user.dp,
      dtmesCompetenc);
      const dados = await IndicadoresController.getComparativoSalarialPorCBO(
        this.user.dp,
        dtmesCompetenc
      );
      this.stateData = (dados && 'stateData' in dados) ? (dados as { stateData: any }).stateData : {};
      this.states = dados.states;
      this.updateLastCacheDate();
      this.$root.$emit('showMessage', {
        text: 'Dados atualizados com sucesso!',
        type: 'success'
      });
    } catch (error) {
      console.error('Erro ao recarregar dados:', error);
      this.$root.$emit('showMessage', {
        text: 'Erro ao atualizar os dados',
        type: 'error'
      });
    } finally {
      this.loading = false;
    }
  }

  private getChartOptions(estrutura: any) {
    return {
      chart: {
        type: 'bar',
        toolbar: {
          show: false
        },
        animations: {
          enabled: true,
          easing: 'easeinout',
          speed: 800,
          animateGradually: {
            enabled: true,
            delay: 150
          },
          dynamicAnimation: {
            enabled: true,
            speed: 350
          }
        }
      },
      plotOptions: {
        bar: {
          borderRadius: 6,
          columnWidth: '70%',
          dataLabels: {
            position: 'top'
          },
          colors: {
            ranges: [{
              from: 0,
              to: 999999999,
              color: undefined
            }]
          }
        }
      },
      colors: ['#1a237e', '#4caf50'],
      dataLabels: {
        enabled: true,
        formatter: function(val: number) {
          return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
          }).format(val);
        },
        offsetY: -20,
        style: {
          fontSize: '12px',
          colors: ["#304758"]
        }
      },
      xaxis: {
        categories: estrutura.cbos.map((cbo: any) => cbo.codigo + '-' + cbo.descricao),
        labels: {
          rotate: -45,
          style: {
            colors: '#546e7a',
            fontSize: '12px'
          }
        },
        tooltip: {
          enabled: true
        }
      },
      yaxis: {
        labels: {
          formatter: function(val: number) {
            return new Intl.NumberFormat('pt-BR', {
              style: 'currency',
              currency: 'BRL',
              minimumFractionDigits: 0,
              maximumFractionDigits: 0
            }).format(val);
          },
          style: {
            colors: '#546e7a'
          }
        }
      },
      legend: {
        position: 'top',
        horizontalAlign: 'center',
        labels: {
          colors: '#546e7a'
        }
      },
      grid: {
        borderColor: '#f1f1f1',
        strokeDashArray: 5,
      },
      tooltip: {
        theme: 'light',
        y: {
          formatter: function(val: number) {
            return new Intl.NumberFormat('pt-BR', {
              style: 'currency',
              currency: 'BRL'
            }).format(val);
          }
        }
      }
    };
  }

  // Métodos auxiliares
  private getMediaVariacao(estrutura: any): number {
    const cbos = estrutura.cbos || [];
    if (!cbos.length) return 0;
    return cbos.reduce((acc: number, cbo: any) => acc + (cbo.diferencaPercentual || 0), 0) / cbos.length;
  }

  private getMediaSalarialOrg(estrutura: any): number {
    const cbos = estrutura.cbos || [];
    if (!cbos.length) return 0;
    return cbos.reduce((acc: number, cbo: any) => acc + (cbo.mediaSalarialOrg || 0), 0) / cbos.length;
  }

  private getMediaSalarialGeral(estrutura: any): number {
    const cbos = estrutura.cbos || [];
    if (!cbos.length) return 0;
    return cbos.reduce((acc: number, cbo: any) => acc + (cbo.mediaSalarialGeral || 0), 0) / cbos.length;
  }

  private getChartSeries(estrutura: any) {
    return [
      {
        name: 'Média do CBO na estrutura',
        data: estrutura.cbos.map((cbo: any) => cbo.mediaSalarialOrg)
      },
      {
        name: 'Média do CBO no Mercado',
        data: estrutura.cbos.map((cbo: any) => cbo.mediaSalarialGeral)
      }
    ];
  }

  private getVariationChartOptions(estrutura: any) {
    return {
      chart: {
        type: 'bar',
        height: 350,
        toolbar: {
          show: false
        }
      },
      plotOptions: {
        bar: {
          horizontal: true,
          distributed: true,
          dataLabels: {
            position: 'center'
          },
          barHeight: '70%'
        }
      },
      dataLabels: {
        enabled: true,
        formatter: function(val: any) {
          return val ? `${val.toFixed(1)}%` : '0%';
        },
        style: {
          colors: ['#fff']
        }
      },
      xaxis: {
        type: 'numeric',
        labels: {
          formatter: function(val: any) {
            return `${val.toFixed(1)}%`;
          }
        },
        title: {
          text: 'Média CBO Estrutura X Média CBO Mercado'
        }
      },
      yaxis: {
        labels: {
          style: {
            fontSize: '12px'
          }
        }
      },
      title: {
        text: 'Média Salarial por CBO comparado ao Mercado em Minas Gerais',
        align: 'center',
        style: {
          fontSize: '16px'
        }
      },
      tooltip: {
        y: {
          formatter: function(val: any) {
            return `${val.toFixed(1)}%`;
          }
        }
      },
      colors: ['#00E396', '#FF4560']
    };
  }

  private getVariationChartSeries(estrutura: any) {
    if (!estrutura || !estrutura.cbos) return [{
      name: 'Média CBO Estrutura X Média CBO Mercado',
      data: []
    }];

    const dados = estrutura.cbos.map(cbo => ({
      x: `${cbo.codigo} - ${cbo.descricao}`,
      y: parseFloat(cbo.diferencaPercentual),
      fillColor: parseFloat(cbo.diferencaPercentual) >= 0 ? '#00E396' : '#FF4560'
    }));

    return [{
      name: 'Média CBO Estrutura X Média CBO Mercado',
      data: dados
    }];
  }

  private getRadarChartOptions(estrutura: any) {
    return {
      chart: {
        type: 'radar',
        height: 350
      },
      title: {
        text: 'Análise Comparativa Salarial',
        align: 'center'
      },
      xaxis: {
        categories: ['Média Salarial', 'Menor Salário', 'Maior Salário']
      },
      yaxis: {
        labels: {
          formatter: function(val: number) {
            return 'R$ ' + val.toLocaleString('pt-BR', { minimumFractionDigits: 2 });
          }
        }
      },
      tooltip: {
        y: {
          formatter: function(val: number) {
            return 'R$ ' + val.toLocaleString('pt-BR', { minimumFractionDigits: 2 });
          }
        }
      },
      colors: ['#008FFB', '#00E396'],
      markers: {
        size: 4,
        colors: ['#fff'],
        strokeColors: '#FF4560',
        strokeWidth: 2,
      }
    };
  }

  private getRadarChartSeries(estrutura: any) {
    // Calculando médias para a estrutura
    const orgMedias = {
      mediaSalarial: this.calcularMedia(estrutura.cbos, 'mediaSalarialOrg'),
      menorSalario: this.calcularMedia(estrutura.cbos, 'menorSalarioOrg'),
      maiorSalario: this.calcularMedia(estrutura.cbos, 'maiorSalarioOrg')
    };

    const geralMedias = {
      mediaSalarial: this.calcularMedia(estrutura.cbos, 'mediaSalarialGeral'),
      menorSalario: this.calcularMedia(estrutura.cbos, 'menorSalarioGeral'),
      maiorSalario: this.calcularMedia(estrutura.cbos, 'maiorSalarioGeral')
    };

    return [
      {
        name: 'Organização',
        data: [orgMedias.mediaSalarial, orgMedias.menorSalario, orgMedias.maiorSalario]
      },
      {
        name: 'Média Geral',
        data: [geralMedias.mediaSalarial, geralMedias.menorSalario, geralMedias.maiorSalario]
      }
    ];
  }

  private calcularMedia(items: any[], campo: string): number {
    return items.reduce((acc, item) => acc + (item[campo] || 0), 0) / items.length;
  }

  private getBulletChartOptions(estrutura: any) {
    return {
      chart: {
        type: 'bar',
        height: 350,
        toolbar: {
          show: false
        }
      },
      plotOptions: {
        bar: {
          horizontal: true,
          distributed: true,
          dataLabels: {
            position: 'center'
          },
          barHeight: '70%'
        }
      },
      dataLabels: {
        enabled: true,
        formatter: function(val: any) {
          return val ? `${val.toFixed(1)}%` : '0%';
        },
        style: {
          colors: ['#fff']
        }
      },
      xaxis: {
        type: 'numeric',
        labels: {
          formatter: function(val: any) {
            return `${val.toFixed(1)}%`;
          }
        },
        title: {
          text: 'Variação Percentual'
        }
      },
      yaxis: {
        labels: {
          style: {
            fontSize: '12px'
          }
        }
      },
      title: {
        text: 'Variação Salarial por CBO',
        align: 'center',
        style: {
          fontSize: '16px'
        }
      },
      tooltip: {
        y: {
          formatter: function(val: any) {
            return `${val.toFixed(1)}%`;
          }
        }
      },
      colors: ['#00E396', '#FF4560']
    };
  }

  private getBulletChartSeries(estrutura: any) {
    if (!estrutura || !estrutura.cbos) return [{
      name: 'Variação',
      data: []
    }];

    const dados = estrutura.cbos.map(cbo => ({
      x: `${cbo.codigo} - ${cbo.descricao}`,
      y: parseFloat(cbo.diferencaPercentual),
      fillColor: parseFloat(cbo.diferencaPercentual) >= 0 ? '#00E396' : '#FF4560'
    }));

    return [{
      name: 'Variação',
      data: dados
    }];
  }

  private renderizarGrafico(estrutura: any) {
    if (!estrutura || !estrutura.cbos || estrutura.cbos.length === 0) {
      console.error('Estrutura inválida ou sem CBOs');
      return;
    }

    // Log para debug
    this.logDadosGrafico(estrutura);

    const options = this.getBulletChartOptions(estrutura);
    const series = this.getBulletChartSeries(estrutura);

    // Verifique se temos dados válidos
    if (series[0].data.length === 0) {
      console.error('Nenhum dado válido para exibir no gráfico');
      return;
    }

    // Renderize o gráfico aqui
    const elementId = `grafico-variacao-${estrutura.id}`;
    const element = document.querySelector(`#${elementId}`);
    
    if (element) {
      const chart = new ApexCharts(element, {
        ...options,
        series: series
      });
      
      chart.render();
    }
  }

  // Método para ser chamado quando os dados da estrutura são carregados
  @Watch('estrutura', { deep: true })
  private onEstruturaChange(estrutura: any) {
    this.$nextTick(() => {
      this.renderizarGrafico(estrutura);
    });
  }

  // Método para debug
  private logDadosGrafico(estrutura: any) {
    console.log('Dados do gráfico:', {
      cbos: estrutura.cbos.map((cbo: any) => ({
        codigo: cbo.codigo,
        descricao: cbo.descricao,
        diferenca: cbo.diferencaPercentual,
        mediaSalarialOrg: cbo.mediaSalarialOrg,
        mediaSalarialGeral: cbo.mediaSalarialGeral
      }))
    });
  }
}
